import React, { useCallback, useEffect, useState } from 'react'
import { Modal, Button, ProgressBar } from 'react-bootstrap-v5'

interface SessionTimeoutDialogProps {
  show: boolean
  remainingTime: number
  totalWarningTime: number
  onContinue: () => void
  onLogout: () => void
  title?: string
  message?: string
  logoutButtonText?: string
  continueButtonText?: string
}

const SessionTimeoutDialog: React.FC<SessionTimeoutDialogProps> = ({
  show,
  remainingTime,
  totalWarningTime,
  onContinue,
  onLogout,
  title = 'Session Timeout Warning',
  message = 'Your session will expire soon due to inactivity. Would you like to continue?',
  logoutButtonText = 'Logout',
  continueButtonText = 'Continue'
}) => {
  const [timeLeft, setTimeLeft] = useState<number>(remainingTime)

  useEffect(() => {
    setTimeLeft(remainingTime)
  }, [remainingTime])

  useEffect(() => {
    if (!show) return

    const interval = setInterval(() => {
      setTimeLeft(prev => {
        if (prev <= 1000) {
          onLogout()
          return 0
        }
        return prev - 1000
      })
    }, 1000)

    return () => clearInterval(interval)
  }, [show, onLogout])

  const formatTime = useCallback((ms: number): string => {
    const seconds = Math.ceil(ms / 1000)
    const minutes = Math.floor(seconds / 60)
    const remainingSeconds = seconds % 60

    return minutes > 0
      ? `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`
      : `${remainingSeconds}`
  }, [])

  const progressPercentage = (timeLeft / totalWarningTime) * 100

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault()
        onContinue()
      } else if (e.key === 'Escape') {
        e.preventDefault()
        onLogout()
      }
    },
    [onContinue, onLogout]
  )

  useEffect(() => {
    if (show) {
      document.addEventListener('keydown', handleKeyDown)
      return () => document.removeEventListener('keydown', handleKeyDown)
    }
  }, [show, handleKeyDown])

  if (!show) return null

  return (
    <Modal
      show={show}
      backdrop="static"
      keyboard={false}
      centered
      size="sm"
      className="session-timeout-modal"
      aria-labelledby="session-timeout-title"
      aria-describedby="session-timeout-description"
    >
      <Modal.Header className="border-0 pb-0">
        <Modal.Title
          id="session-timeout-title"
          className="d-flex align-items-center"
        >
          <i
            className="bi bi-clock-history text-warning me-2"
            aria-hidden="true"
          ></i>
          {title}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body className="pt-2">
        <div className="text-center">
          <div className="mb-3">
            <i
              className="bi bi-exclamation-triangle text-warning display-1"
              aria-hidden="true"
            ></i>
          </div>

          <p id="session-timeout-description" className="mb-3">
            {message}
          </p>

          <div className="mb-3">
            <div className="d-flex justify-content-between align-items-center mb-2">
              <small className="text-muted">Session expires in:</small>
              <strong className="text-danger">{formatTime(timeLeft)}</strong>
            </div>

            <ProgressBar
              now={progressPercentage}
              variant={progressPercentage > 50 ? 'warning' : 'danger'}
              className="mb-2"
              style={{ height: '8px' }}
              aria-label={`${Math.ceil(timeLeft / 1000)} seconds remaining`}
            />
          </div>

          <div className="alert alert-info small mb-3" role="alert">
            <i className="bi bi-info-circle me-1" aria-hidden="true"></i>
            Press <kbd>Enter</kbd> to continue or <kbd>Esc</kbd> to logout
          </div>
        </div>
      </Modal.Body>

      <Modal.Footer className="border-0 pt-0">
        <div className="d-grid gap-2 d-md-flex justify-content-md-end w-100">
          <Button
            variant="outline-secondary"
            onClick={onLogout}
            className="me-md-2"
            size="sm"
          >
            <i className="bi bi-box-arrow-right me-1" aria-hidden="true"></i>
            {logoutButtonText}
          </Button>
          <Button variant="primary" onClick={onContinue} autoFocus size="sm">
            <i className="bi bi-arrow-clockwise me-1" aria-hidden="true"></i>
            {continueButtonText}
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  )
}

export {SessionTimeoutDialog};